<template>
  <b-card
    header="Users"
    class="user-modules"
  >
    <template #header>
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">
          {{ getCurrentTitle }}
        </h4>
        <b-button
          :id="`${getCurrentTitle}-popover-button-${user.id}`"
          variant="outline-primary"
          size="sm"
        >
          <font-awesome-icon :icon="['fas', 'plus']" />
          {{ `Assign ${getCurrentTitle}` }}
        </b-button>
        <permission-card-popover
          v-if="currentDataObject"
          :local-permissions="localPermissions"
          :current-id-key="getCurrentGuidKey"
          :current-data="currentDataObject"
          :title="getCurrentTitle"
          :is-item-was-deleted="isItemWasDeleted"
          :type="type"
          :user="user"
          @add-to-permissions="addToPermissions"
        />
      </div>
    </template>
    <b-card-text>
      <div class="input__wrapper">
        <b-button
          v-if="searchValue.length"
          class="clear__button"
          variant="outlined"
          size="sm"
          @click="clearSearchValue"
        >
          <font-awesome-icon :icon="['fas', 'close']" />
        </b-button>
        <b-form-input
          v-model="searchValue"
          placeholder="Start typing the name of the entity"
          class="search__input"
          debounce="500"
        />
        <no-content
          :text="`Unable to find ${getCurrentTitle}`"
          :show="!searchByEntries || !currentData.length"
        />
      </div>
      <recycle-scroller
        v-if="searchByEntries && currentData.length"
        v-slot="{ item }"
        class="scroller"
        :items="searchByEntries"
        :item-size="45"
        :key-field="getCurrentGuidKey"
      >
        <div
          :id="`tooltip-${item.label}`"
          class="list__item"
        >
          <b-tooltip
            v-if="type !== 'modules'"
            :target="`tooltip-${item.label}`"
            triggers="hover"
          >
            {{ getTooltipTitle(item) }}
          </b-tooltip>
          <span
            class="mr-auto entity-name"
            data-toggle="tooltip"
            data-placement="bottom"
            v-html="formattedTitle(item.label)"
          />
          <b-spinner
            v-if="item.isLoading"
            :key="item.isLoading"
            class="accordion__spinner"
            type="grow"
          />
          <b-form-select
            v-else
            v-model="item.currentUserPermissionLevel"
            style="width: 6rem"
            :options="permissionTypes"
            @change="onChange(item)"
          />
        </div>
      </recycle-scroller>
    </b-card-text>
  </b-card>
</template>

<script>
import NoContent from '../../../../core/components/NoContent.vue';
import {createNamespacedHelpers} from 'vuex';
import {highlightText} from '@/core/utils';
import PermissionCardPopover from '@/modules/Security/components/PermissionCardComponents/PermissionCardPopover.vue';

const {
  mapState: dataManagementMapState,
  mapActions: dataManagementMapActions,
} = createNamespacedHelpers('dataManagement');
const {mapState: sidebarMapState} = createNamespacedHelpers('sidebar');
export default {
  name: 'PermissionCard',
  components: {
    PermissionCardPopover,
    NoContent,
  },
  props: {
    type: String,
    user: Object,
    localPermissions: Array,
  },
  data() {
    return {
      isItemWasDeleted: false,
      keyword: '',
      currentData: null,
      currentDataObject: null,
      copyData: null,
      searchValue: '',
      isLoading: false,
      isPopoverOpened: false,
    };
  },
  computed: {
    ...dataManagementMapState(['permissionTypes']),
    ...sidebarMapState({
      tables: state => state.tablesList,
      folders: state => state.foldersList,
      modules: state => state.modulesList,
    }),
    searchByEntries() {
      const inputValue = this.searchValue.toLowerCase();
      if (inputValue.length) {
        return this.copyData.filter(el => el.label.toLowerCase().includes(inputValue));
      }
      return this.currentData;
    },
    getCurrentGuidKey() {
      switch (this.type) {
      case 'modules' :
        return 'moduleGuid';
      case 'folders':
        return 'folderGuid';
      default:
        return 'entityGuid';
      }
    },
    getCurrentTitle() {
      switch (this.type) {
      case 'modules' :
        return 'Modules';
      case 'folders':
        return 'Folders';
      default:
        return 'Tables';
      }
    },
  },
  methods: {
    ...dataManagementMapActions(['updatePermission', 'createPermission', 'deletePermission']),
    addToPermissions({item, newItem}) {
      this.searchByEntries.push(item);
      this.copyData.push(item);
      this.$emit('change-local-permissions', newItem);

      delete this.currentDataObject[this.getCurrentGuidKey];
    },
    isSetPermission(object) {
      return !['none'].includes(object.currentUserPermissionLevel);
    },
    clearSearchValue() {
      this.searchValue = '';
    },
    getTooltipTitle(item) {
      switch (this.type) {
      case 'folders':
        return `${this.modules[item.moduleGuid]?.label} ➪ ${this.folders[item.folderGuid].label}`;
      case 'tables':
        return `${this.modules[item.moduleGuid]?.label} ➪ ${this.folders[item.folderGuid].label} ➪ ${this.tables[item.entityGuid].label}`;
      }
    },
    formattedTitle(label) {
      return highlightText(this.searchValue, label);
    },
    async onChange(item) {
      item.isLoading = true;
      const requestData = this.localPermissions.find(el => el.objectGuid === item[this.getCurrentGuidKey]);
      const isCurrentPermissionLevelNone = item.currentUserPermissionLevel === 'none';

      if (isCurrentPermissionLevelNone) {
        await this.deletePermission(requestData.permissionGuid);
        this.isItemWasDeleted = !this.isItemWasDeleted;
        this.$emit('remove-from-local-permissions', item[this.getCurrentGuidKey]);
      } else {
        await this.updatePermission({
          ...requestData,
          objectGuid: item[this.getCurrentGuidKey],
          permissionLevel: item.currentUserPermissionLevel,
        });
      }
      item.isLoading = false;
      this.currentData = this.currentData.filter(el => this.isSetPermission(el));

    },
  },
  mounted() {
    const map = {};
    for (const item of this.localPermissions) {
      if (item.objectGuid in this[this.type]) {
        this[this.type][item.objectGuid].currentUserPermissionLevel = item.permissionLevel;
        map[item.objectGuid] = this[this.type][item.objectGuid];
      }
    }

    this.currentDataObject = map;
    this.currentData = Object.values(map).filter(el => this.isSetPermission(el));
    this.copyData = JSON.parse(JSON.stringify(this.currentData));
  },
};
</script>

<style lang="scss">
#modules-popover, #folders-popover, #tables-popover {
  font-size: .75rem;

  .popover-body {
    padding: 0;
  }
}

.user-modules {
  overflow: auto;
  border-radius: 0;

  .card-body {
    position: relative;
    padding: 0 0 7px 0;
  }

  .card-header {
    padding: 7px;
  }

  .card-body {
    overflow: auto;
  }
}

.input__wrapper {
  background-color: #fff;
  padding: 7px;
  z-index: 10;
  position: sticky;
  top: 0;
}

.list-group {
  position: relative;
  gap: 1px;
}

.list__item {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  padding: 5px 10px !important;
  border: none !important;
  border-bottom: 1px solid #eee !important;
}

.entity-name {
  width: 90%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.clear__button {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(-50%, -50%);
}

.scroller {
  height: 394px;

  @media (max-width: 1200px) {
    height: 230px;
  }
}
</style>